/* global REACT_APP_PUBLIC_META_TEST_EVENT_CODE */
// Meta Pixel propio de Plick marketing — el MISMO pixel que plick.com.mx (372831828010062).
// Es el pixel de PRODUCCIÓN: no existe uno de pruebas.
//
// El pixel se carga en TODOS los ambientes con tracking habilitado, incluido PRUEBAS
// (`vite.config.dev.ts` tiene `ENABLE_TRACKING: true`). Esto es deliberado: Meta no admite
// `localhost`, así que el único lugar donde se puede validar de verdad que un evento sale y
// llega es el ambiente de pruebas desplegado, que sí tiene HTTPS válido y es accesible desde
// la nube. Validar en local NO es una opción.
//
// Como el pixel es el de producción, los eventos de pruebas se marcan con `test_event_code`:
// aparecen en Events Manager → "Probar eventos" y NO entran a optimización de campañas,
// audiencias ni ROAS. Sin esa marca, cada registro de QA metería un `Purchase` falso a las
// campañas reales.
//
// OJO: `test_event_code` solo cubre el pixel de NAVEGADOR. Cuando llegue la CAPI server-side
// (PLI-13) necesita su PROPIO parámetro de prueba, o los eventos de servidor de pruebas sí
// van a contaminar el pixel real.
export const META_PIXEL_ID = '372831828010062';

// Vacío en producción y en local (donde Meta no transmite). Con valor solo en pruebas.
const testEventCode = (): string => {
  try {
    return REACT_APP_PUBLIC_META_TEST_EVENT_CODE || '';
  } catch {
    return '';
  }
};

// Se adjunta como parte del custom data del evento; Meta lo interpreta como marcador de
// "evento de prueba". Devuelve objeto vacío cuando no aplica, para no alterar el payload real.
const testEventParams = (): Record<string, string> => {
  const code = testEventCode();
  return code ? { test_event_code: code } : {};
};

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push?: Fbq;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let initialized = false;

const ensureFbqStub = (): Fbq => {
  if (window.fbq) return window.fbq;
  const fbq = ((...args: unknown[]) => {
    // Antes de que cargue fbevents.js los llamados se encolan; después se ejecutan directo.
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  }) as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
};

export const initMetaPixel = (pixelId: string): void => {
  if (initialized || typeof window === 'undefined' || !pixelId) return;
  initialized = true;
  ensureFbqStub();
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq?.('init', pixelId);
};

export const isMetaPixelInitialized = (): boolean => initialized;

const newEventId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // noop — entorno sin crypto.randomUUID
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Evento de conversión del flujo FREE: paridad EXACTA con la página de aterrizaje que
// reemplazamos (plick.com.mx/activation-plick-basico), que disparaba el evento custom
// `registro_plan_basico` con eventID y SIN advanced matching. El eventID único por conversión
// deja listo el dedup contra una futura CAPI server-side que use el mismo ID.
// OJO (bug 2026-08-29): NO re-inicializar el pixel con `fbq('init', pixelId, {em, ph})` en
// este punto — el re-init con PII en texto plano deja al pixel en estado `pendingConfigs` y
// bloquea el envío de CUALQUIER evento posterior. El matching avanzado se hará server-side
// (PLI-13 / CAPI), no aquí.
export const metaPixelTrackRegistration = (): string | null => {
  if (!initialized || typeof window === 'undefined' || !window.fbq) return null;
  try {
    const eventID = newEventId();
    // El eventID va en el CUARTO argumento, no en el tercero. Meta solo lo lee para
    // deduplicar (viaja como `eid=`) desde esa posición; dentro del custom data (3er arg)
    // llega como `cd[eventID]` y la CAPI lo IGNORA — o sea que el dedup no existiría y el
    // mismo registro se contaría dos veces cuando entre la CAPI server-side (PLI-13).
    window.fbq('trackCustom', 'registro_plan_basico', testEventParams(), { eventID });
    return eventID;
  } catch {
    return null;
  }
};

// Conversión del flujo PAGA (registro legacy con token): paridad EXACTA con la página de
// aterrizaje que reemplazamos (plick.com.mx/activation-plick), que disparaba el evento
// estándar Purchase con value/currency fijos — sin eventID y sin advanced matching, porque
// la página original no los enviaba. NO cambiar por un evento custom ni agregar propiedades.
export const PAID_REGISTRATION_VALUE = 700.0;
export const PAID_REGISTRATION_CURRENCY = 'MXN';

export const metaPixelTrackPaidRegistration = (): string | null => {
  if (!initialized || typeof window === 'undefined' || !window.fbq) return null;
  try {
    // El eventID también aquí, y por la MISMA razón que en el flujo free: este `Purchase`
    // mueve dinero y es el que peor tolera contarse doble. La página original no lo mandaba
    // (usaba una constante en el 3er argumento, que no deduplica nada), así que esto NO es
    // paridad con ella — es la corrección de ese defecto.
    const eventID = newEventId();
    window.fbq(
      'track',
      'Purchase',
      { value: PAID_REGISTRATION_VALUE, currency: PAID_REGISTRATION_CURRENCY, ...testEventParams() },
      { eventID },
    );
    return eventID;
  } catch {
    return null;
  }
};
